/**
 * EDD — entity 내 동일 contact 의 다중 직책 + BO(지분/지배) 정보 조회.
 */
import {
  getActiveEntityOfficersAll,
  getActiveMembers,
  getActiveBeneficialOwners,
} from '@/features/startTask/preset_task/sg/components/sgDocGenEntityApi';
import { buildShareholderRows, formatPct } from '../cdd/cddShareholders';

const BO_TYPE_LABEL: Record<number, string> = {
  1: 'Direct ownership',
  2: 'Indirect ownership',
  3: 'Direct & Indirect ownership',
  4: 'Control by other means',
};

function normName(s: string): string {
  return String(s || '').trim().toUpperCase().replace(/\s+/g, ' ');
}

function pickName(row: any): string {
  return String(
    row?.name ||
    row?.contactName ||
    row?.fullName ||
    [row?.firstName, row?.middleName, row?.surname].filter(Boolean).join(' ') ||
    '',
  ).trim();
}

function matchPerson(row: any, contactCd: string, name: string): boolean {
  const cd = String(row?.contactCd || row?.CONTACT_CD || '').trim();
  if (contactCd && cd && cd === contactCd) return true;
  if (name && normName(pickName(row)) === normName(name)) return true;
  return false;
}

function resolveBoControl(b: any): string {
  const desc = String(b?.boTypeDescription || b?.BOTypeDescription || '').trim();
  if (desc) return desc;
  const id = Number(b?.boTypeId ?? b?.BOTypeId ?? 0);
  if (BO_TYPE_LABEL[id]) return BO_TYPE_LABEL[id];
  const legacy = String(b?.typeOfBo || '').trim().toUpperCase();
  if (legacy === 'D') return BO_TYPE_LABEL[1];
  if (legacy === 'I') return BO_TYPE_LABEL[2];
  if (legacy === 'DI') return BO_TYPE_LABEL[3];
  if (legacy === 'C') return BO_TYPE_LABEL[4];
  if (b?.isJointArrangement) return 'Joint arrangement';
  if (b?.isSeniorManagement) return 'Senior management';
  return '';
}

function addRole(set: Set<string>, label: string) {
  const t = String(label || '').trim();
  if (!t || /^contact$/i.test(t)) return;
  // 역할 표기 통일
  const mapped = /^shareholder$/i.test(t) ? 'Member' : t;
  set.add(mapped);
}

export interface EddPersonEntityInfo {
  roles: string[];
  rolesText: string;
  isBo: boolean;
  /** Additional Information on Customer and Beneficial Owner 칸에 넣을 문장. */
  additionalInfo: string;
}

function buildBoAdditionalInfo(parts: {
  ownershipPct?: string;
  ownershipControl?: string;
  controlDetails?: string;
}): string {
  const lines: string[] = [];
  if (parts.ownershipPct) lines.push(`Ownership / Shareholding: ${parts.ownershipPct}`);
  if (parts.ownershipControl) lines.push(`Type of ownership/control: ${parts.ownershipControl}`);
  if (parts.controlDetails) lines.push(`Control structure / criteria: ${parts.controlDetails}`);
  return lines.join('\n');
}

export async function resolveEddPersonEntityInfo(
  entityCd: string,
  person: { contactCd?: string; name?: string; positionName?: string },
): Promise<EddPersonEntityInfo> {
  const empty: EddPersonEntityInfo = {
    roles: [],
    rolesText: '',
    isBo: false,
    additionalInfo: '',
  };
  const ecd = String(entityCd || '').trim();
  if (!ecd) return empty;

  const contactCd = String(person.contactCd || '').trim();
  const name = String(person.name || '').trim();
  const roles = new Set<string>();
  addRole(roles, person.positionName || '');

  let ownershipPct = '';
  let ownershipControl = '';
  let controlDetails = '';
  let isBo = false;

  try {
    const [offRes, memRes, boRes] = await Promise.all([
      getActiveEntityOfficersAll(ecd).catch(() => null),
      getActiveMembers(ecd).catch(() => null),
      getActiveBeneficialOwners(ecd).catch(() => null),
    ]);

    const officers = (offRes as any)?.data?.officers || [];
    for (const o of officers) {
      if (!matchPerson(o, contactCd, name)) continue;
      const positions = Array.isArray(o.positions) ? o.positions : [];
      if (positions.length) {
        for (const p of positions) addRole(roles, p?.positionName || p?.POSITION_NAME || '');
      } else {
        addRole(roles, o.positionName || o.POSITION_NAME || '');
      }
    }

    const members = (memRes as any)?.data?.members || [];
    const matchedMembers = members.filter((m: any) => matchPerson(m, contactCd, name));
    if (matchedMembers.length) addRole(roles, 'Member');

    if (matchedMembers.length && members.length) {
      const allRows = buildShareholderRows(members);
      const nameKey = normName(name || pickName(matchedMembers[0]));
      const mine = allRows.filter((r) => normName(r.name) === nameKey);
      if (mine.length) {
        let sum = 0;
        for (const r of mine) {
          const n = parseFloat(String(r.pct).replace('%', ''));
          if (Number.isFinite(n)) sum += n;
        }
        if (sum > 0) ownershipPct = formatPct(sum);
      }
    }

    const bos = (boRes as any)?.data?.beneficialOwners || [];
    const matchedBo = bos.find((b: any) => matchPerson(b, contactCd, name));
    if (matchedBo) {
      isBo = true;
      addRole(roles, 'Beneficial Owner');
      const pctRaw = String(
        matchedBo.ownershipPercent ??
        matchedBo.ownershipPct ??
        matchedBo.OWNERSHIP_PERCENT ??
        '',
      ).trim();
      if (pctRaw) {
        const n = parseFloat(pctRaw.replace('%', ''));
        ownershipPct = Number.isFinite(n) ? formatPct(n) : pctRaw;
      }
      ownershipControl = resolveBoControl(matchedBo);
      controlDetails = String(
        matchedBo.criteriaText ||
        matchedBo.CRITERIA_TEXT ||
        matchedBo.controlStructure ||
        matchedBo.remarks ||
        '',
      ).trim();
    }
  } catch (e) {
    console.warn('[EDD] resolve person entity roles failed', e);
  }

  const roleList = [...roles];
  // Additional Information = BO 관련 보충자료 칸.
  // Director/Member 만이면 자동채움하지 않음 — Beneficial Owner 일 때만 지분·지배구조 기입.
  const additionalInfo = isBo
    ? buildBoAdditionalInfo({ ownershipPct, ownershipControl, controlDetails })
    : '';

  return {
    roles: roleList,
    rolesText: roleList.join(', '),
    isBo,
    additionalInfo,
  };
}
